import { useEffect, useState } from "react";
import { postQuestion, patchQuestion } from "@/api/questionnaireQuestions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export default function QuestionForm({ question, allQuestions = [], onSave }) {
    const [form, setForm] = useState({
        questionText: question?.questionText || "",
        questionType: question?.questionType || "bool",
        reliesOnId:
            question?.reliesOnId || question?.reliesOn?.id || null,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const parentQuestions = allQuestions.filter(
        (q) => !q.reliesOn && q.questionType === "bool" && (!question || q.id !== question.id)
    );

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const payload = {
            questionText: form.questionText,
            questionType: form.questionType,
            reliesOnId: form.reliesOnId || null,
        };

        const result = question?.id
            ? await patchQuestion(question.id, payload)
            : await postQuestion(payload);

        if (result) {
            onSave?.(result);
        } else {
            setError("Something went wrong. Please try again.");
        }

        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div>
                <Label htmlFor="questionText" className="mb-2 block">Question Text</Label>
                <Textarea
                    id="questionText"
                    value={form.questionText}
                    onChange={(e) => handleChange("questionText", e.target.value)}
                    required
                />
            </div>

            <div>
                <Label htmlFor="questionType" className="mb-2 block">Type</Label>
                <Select
                    value={form.questionType}
                    onValueChange={(val) => handleChange("questionType", val)}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="bool">Yes/No</SelectItem>
                        <SelectItem value="string">Text Field</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div>
                <Label htmlFor="reliesOnId" className="mb-2 block">Controlled by</Label>
                <Select
                    value={form.reliesOnId?.toString() || "none"}
                    onValueChange={(val) => handleChange("reliesOnId", val === "none" ? null : parseInt(val))}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {parentQuestions.map((q) => (
                            <SelectItem key={q.id} value={String(q.id)}>
                                <span className="truncate block max-w-xs">{q.questionText}</span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex justify-end">
                <Button type="submit" disabled={loading}>
                    {question ? "Update Question" : "Add Question"}
                </Button>
            </div>
        </form>
    );
}
